// src/receptionist/merchant.ts
//
// The merchant configuration model. Everything the AI is allowed to say comes
// from here: it quotes this and nothing else (PRD §3.1). The BE contract does
// not exist yet, so the fixture below stands in — same pattern as @oura/data.

export type MerchantCategory = "clinic" | "salon" | "spa" | "dental";
export type Tone = "clinic_formal" | "salon_casual";

export interface Service {
    id: string;
    name: string;
    durationMinutes: number;
    price: number;
    firstVisitPrice: number;
    /** Off = the AI describes it but never offers it for booking. */
    bookable: boolean;
    /** Its picture (ourteam-backend#6681), sent when the AI asks which treatment.
     *  Set and cleared only through the image endpoints, never by a save. */
    imageKey?: string | null;
    /** A signed link to show it; read-only, expires after a few hours. */
    imageUrl?: string | null;
}

export interface OpeningHours {
    /** 0 = Sunday. Null = closed that day. */
    day: number;
    open: string | null;
    close: string | null;
}

export interface StaffMember {
    id: string;
    name: string;
    role: string;
    phone: string;
    consoleAccess: boolean;
}

export interface EscalationRule {
    key: string;
    label: string;
    enabled: boolean;
    /** Locked rules cannot be switched off. */
    locked?: boolean;
    /** Why it is locked, shown under the label. */
    lockNote?: string;
}

export interface MerchantConfig {
    id: string;
    name: string;
    category: MerchantCategory;
    outlet: string;
    address: string;
    whatsappNumber: string;
    /** Whether the number has completed WABA migration + display-name review. */
    whatsappConnected: boolean;
    approvedTemplates: number;
    languages: string[];
    hours: OpeningHours[];
    closedDates: { date: string; reason: string }[];
    services: Service[];
    cancellationPolicy: string;
    depositPolicy: string;
    faqNotes: { label: string; body: string }[];
    tone: Tone;
    remind24h: boolean;
    remind2h: boolean;
    dropOffNudge: boolean;
    afterHoursCapture: boolean;
    digestTime: string;
    escalationRules: EscalationRule[];
    staffChannel: { name: string; connected: boolean; memberCount: number };
    alertOnBooking: boolean;
    alertOnEscalation: boolean;
    alertMorningDigest: boolean;
    unconfirmedChaseHours: number | null;
    people: StaffMember[];
    handBackKeyword: string;
    handBackQuietHours: number;
}

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const TONE_LABELS: Record<Tone, { label: string; description: string; greeting: string }> = {
    clinic_formal: {
        label: "Clinic formal",
        description: "Professional and precise, careful with anything health related.",
        greeting: "Hello! Welcome to {business}. I can help with treatments, prices and bookings. How may I help you today?",
    },
    salon_casual: {
        label: "Salon casual",
        description: "Warm and chatty, a little more informal.",
        greeting: "Hi! Welcome to {business} 👋 Ask me about our services, prices or booking a slot!",
    },
};

/** Clinics cannot switch these off. Mirrors the medical boundary in PRD §3.7. */
export const LOCKED_FOR_CLINICS = ["medical_suitability", "complaints"];

export function defaultEscalationRules(category: MerchantCategory): EscalationRule[] {
    const clinical = category === "clinic" || category === "dental";
    return [
        { key: "medical_suitability", label: "Medical or treatment-suitability questions", enabled: true, locked: clinical, lockNote: clinical ? "Always on for a clinic." : undefined },
        { key: "complaints", label: "Complaints", enabled: true, locked: clinical, lockNote: clinical ? "Always on for a clinic." : undefined },
        { key: "price_negotiation", label: "Price negotiation and package discounts", enabled: true },
        { key: "specific_staff", label: "Requests for a specific staff member", enabled: true },
        { key: "unknown", label: "Anything the AI cannot answer from this console", enabled: true, locked: true, lockNote: "The AI never guesses." },
    ];
}

/** Demo merchant. Replaced by the BE read once the contract lands. */
export function demoMerchant(): MerchantConfig {
    return {
        id: "ownlab-secret-skin",
        name: "Ownlab Secret Skin",
        category: "clinic",
        outlet: "Publika",
        address: "Level G2, Publika Shopping Gallery, Solaris Dutamas, Kuala Lumpur",
        whatsappNumber: "+60 12-XXX XXXX",
        whatsappConnected: true,
        approvedTemplates: 4,
        languages: ["English", "Bahasa Melayu", "中文"],
        hours: [
            { day: 0, open: null, close: null },
            { day: 1, open: "10:00", close: "19:00" },
            { day: 2, open: "10:00", close: "19:00" },
            { day: 3, open: "10:00", close: "19:00" },
            { day: 4, open: "10:00", close: "19:00" },
            { day: 5, open: "10:00", close: "19:00" },
            { day: 6, open: "10:00", close: "19:00" },
        ],
        closedDates: [{ date: "2026-09-16", reason: "Malaysia Day" }],
        services: [
            { id: "sig", name: "Signature Deep Cleansing Facial", durationMinutes: 90, price: 268, firstVisitPrice: 188, bookable: true },
            { id: "hydra", name: "Hydra Glow Facial", durationMinutes: 75, price: 328, firstVisitPrice: 248, bookable: true },
            { id: "peel", name: "Chemical Peel", durationMinutes: 60, price: 398, firstVisitPrice: 398, bookable: true },
            { id: "led", name: "LED Therapy Add-on", durationMinutes: 20, price: 88, firstVisitPrice: 88, bookable: false },
        ],
        cancellationPolicy: "Cancellations under 24 hours before the appointment may forfeit the booking fee.",
        depositPolicy: "No deposit required.",
        faqNotes: [
            { label: "Parking", body: "Publika basement parking. Take the Zone B lifts to Level G2." },
            { label: "Promotion of the month", body: "First-visit Signature Facial at RM188, September only." },
        ],
        tone: "clinic_formal",
        remind24h: true,
        remind2h: true,
        dropOffNudge: true,
        afterHoursCapture: true,
        digestTime: "10:00",
        escalationRules: defaultEscalationRules("clinic"),
        staffChannel: { name: "Ownlab Front Desk", connected: true, memberCount: 3 },
        alertOnBooking: true,
        alertOnEscalation: true,
        alertMorningDigest: true,
        unconfirmedChaseHours: 2,
        people: [
            { id: "p1", name: "Aina", role: "Owner", phone: "012-XXX XXXX", consoleAccess: true },
            { id: "p2", name: "Front desk", role: "Reception", phone: "017-XXX XXXX", consoleAccess: false },
        ],
        handBackKeyword: "#back",
        handBackQuietHours: 24,
    };
}
